import { motion } from "framer-motion";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { getProductBySlug } from "../data/products";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { SEO, ProductSchema } from "../components/SEO";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const product = getProductBySlug(slug);

  if (!product) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#2D2D2D] mb-4">Produit non trouvé</h1>
          <Link to="/nos-produits" className="text-[#8BC34A] hover:underline">
            Retour aux produits
          </Link>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-[#F5F5F0]"
    >
      <Navbar />
      <SEO
        path={`/produit/${slug}`}
        title={`${product.name} — Bambou REBO La Réunion`}
        description={product.shortDescription}
        image={product.image}
      />
      <ProductSchema
        name={product.name}
        description={product.shortDescription}
        image={product.image}
      />
      <main className="pt-24">
        <section className="py-12 md:py-20">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            {/* Back button */}
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={() => navigate("/nos-produits")}
              className="flex items-center gap-2 text-[#3A3A3A] hover:text-[#8BC34A] mb-8 transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
              Retour aux produits
            </motion.button>

            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
              {/* Image */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl border-2 border-[#8BC34A]/20">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </motion.div>

              {/* Content */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <span className="text-[#8BC34A] text-sm font-semibold tracking-widest uppercase mb-4 block">
                  {product.categoryLabel}
                </span>
                <h1
                  className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#2D2D2D] mb-4"
                  style={{ fontFamily: "'DM Serif Display', serif" }}
                  data-testid="product-title"
                >
                  {product.name}
                </h1>
                <p className="text-lg text-[#8BC34A] font-medium mb-6 italic">
                  Longueurs disponibles: {product.dimensions}
                </p>
                <div className="prose prose-lg text-[#3A3A3A] mb-8">
                  {product.description.split('\n\n').map((paragraph, idx) => (
                    <p key={`desc-${idx}-${paragraph.slice(0,20)}`} className="mb-4 leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>

                {/* Features */}
                {product.features && product.features.length > 0 && (
                  <div className="mb-8">
                    <h3 className="font-semibold text-[#2D2D2D] mb-4">Caractéristiques</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {product.features.map((feature) => (
                        <div key={feature} className="flex items-center gap-2 text-[#3A3A3A]">
                          <div className="w-2 h-2 rounded-full bg-[#8BC34A]" />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Link
                  to="/demande-de-devis"
                  className="inline-flex items-center gap-2 bg-[#8BC34A] text-[#2D2D2D] rounded-full px-8 py-4 text-lg font-semibold hover:bg-[#A5D66A] transition-all duration-300 shadow-lg hover:shadow-xl"
                  data-testid="request-quote-btn"
                >
                  Je demande un devis
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </motion.div>
            </div>

          </div>
        </section>
      </main>
      <Footer />
    </motion.div>
  );
}
